import React, { useState, useEffect } from "react";

import { useParams } from "react-router-dom/cjs/react-router-dom";

import Wrapper from "../components/wrapper";
import { Layout, Typography, DatePicker, Table, Tooltip } from "antd";
import { ArrowUpOutlined, ArrowDownOutlined } from "@ant-design/icons";

import { fetchCityMonitoring } from "../redux/actions/adminDashBoard";
import { useDispatch, useSelector } from "react-redux";

const { RangePicker } = DatePicker;
const { Content: LayoutContent } = Layout;
const { Title } = Typography;
const { Column, ColumnGroup } = Table;

const CityMonitoring = () => {
  const { province } = useParams();
  const dispatch = useDispatch();

  const { loading, data } = useSelector(({ AdminDashBoardReducer }) => ({
    loading: AdminDashBoardReducer.loading,
    data: AdminDashBoardReducer.cityMonitoring,
  }));

  const [timeRange, setTimeRange] = useState(null);

  useEffect(() => {
    if (timeRange) {
      const startDate = timeRange[0].format("YYYY-MM-DD");
      const endDate = timeRange[1].format("YYYY-MM-DD");

      dispatch(
        fetchCityMonitoring({
          defaultDate: false,
          fromDate: startDate,
          toDate: endDate,
          province: province,
        })
      );
    } else {
      dispatch(fetchCityMonitoring({ defaultDate: true, province: province }));
    }
  }, [dispatch, timeRange, province]);

  const handleTimeChange = (value) => {
    setTimeRange(value);
  };

  const renderContent = () => {
    return (
      <LayoutContent>
        <div>
          <Title level={2}>{province}</Title>
        </div>
        <div style={{ marginBottom: "10px", fontSize: "14px", color: "#888" }}>
          <Tooltip title="This filter applies to Rider Count Top-Up, Rider Count Online, and the numbers for Orders Delivered, Undelivered, and Cancelled, adjusting these values based on the selected time range.">
            <span>Filter affects Rider and Order metrics.</span>
          </Tooltip>
        </div>
        <div>
          <RangePicker value={timeRange} onChange={handleTimeChange} />
          <Table
            dataSource={data}
            bordered
            scroll={{ x: 1500 }}
            loading={loading}
            pagination={{ pageSize: 7 }}
          >
            <Column
              title="City"
              dataIndex="city"
              key="city"
              sorter={(a, b) => a.city.localeCompare(b.city)}
            />
            <Column
              title="Merchant"
              dataIndex="merchantCount"
              key="merchantCount"
              sorter={(a, b) => a.merchantCount - b.merchantCount}
              width={100}
            />
            <Column
              title="Own Branch"
              dataIndex="isBranch"
              key="isBranch"
              render={(value) => (value === 1 ? "Yes" : "No")}
              filters={[
                { text: "Yes", value: 1 },
                { text: "No", value: 0 },
              ]}
              onFilter={(value, record) => record.isBranch === value}
            />
            <ColumnGroup title="Rider Top up">
              <Column
                title="Prev."
                dataIndex="rider_prev_topup_count"
                key="rider_prev_topup_count"
                render={(_, record) => record.riderTopUp?.prev_count ?? "-"}
                sorter={(a, b) => {
                  const prevCountA = a.riderTopUp?.prev_count || 0;
                  const prevCountB = b.riderTopUp?.prev_count || 0;
                  return prevCountA - prevCountB;
                }}
              />
              <Column
                title="Curr."
                dataIndex="rider_curr_topup_count"
                key="rider_curr_topup_count"
                render={(_, record) => record.riderTopUp?.curr_count ?? "-"}
                sorter={(a, b) => {
                  const currCountA = a.riderTopUp?.curr_count || 0;
                  const currCountB = b.riderTopUp?.curr_count || 0;
                  return currCountA - currCountB;
                }}
              />
              <Column
                title="Chg %"
                dataIndex="rider_topup_change_percentage"
                key="rider_topup_change_percentage"
                render={(_, record) => {
                  const change = parseFloat(
                    record.riderTopUp?.percentage_change
                  );

                  if (isNaN(change)) {
                    return "-";
                  }

                  if (change > 0) {
                    return (
                      <span style={{ color: "green" }}>
                        <ArrowUpOutlined style={{ marginRight: 4 }} />
                        {change}%
                      </span>
                    );
                  }

                  if (change < 0) {
                    return (
                      <span style={{ color: "red" }}>
                        <ArrowDownOutlined style={{ marginRight: 4 }} />
                        {change}%
                      </span>
                    );
                  }

                  return <span style={{ color: "black" }}>{change}%</span>;
                }}
                sorter={(a, b) => {
                  const changeA = parseFloat(a.riderTopUp?.percentage_change);
                  const changeB = parseFloat(b.riderTopUp?.percentage_change);
                  return changeA - changeB;
                }}
              />
            </ColumnGroup>

            <ColumnGroup title="Rider Online">
              <Column
                title="Prev."
                dataIndex="rider_prev_online_count"
                key="rider_prev_online_count"
                render={(_, record) => record.riderOnline?.prev_count ?? "-"}
                sorter={(a, b) => {
                  const prevCountA = a.riderOnline?.prev_count || 0;
                  const prevCountB = b.riderOnline?.prev_count || 0;
                  return prevCountA - prevCountB;
                }}
              />
              <Column
                title="Curr."
                dataIndex="rider_curr_online_count"
                key="rider_curr_online_count"
                render={(_, record) => record.riderOnline?.curr_count ?? "-"}
                sorter={(a, b) => {
                  const currCountA = a.riderOnline?.curr_count || 0;
                  const currCountB = b.riderOnline?.curr_count || 0;
                  return currCountA - currCountB;
                }}
              />
              <Column
                title="Chg %"
                dataIndex="rider_online_change_percentage"
                key="rider_online_change_percentage"
                render={(_, record) => {
                  const change = parseFloat(
                    record.riderOnline?.percentage_change
                  );

                  if (isNaN(change)) {
                    return "-";
                  }

                  if (change > 0) {
                    return (
                      <span style={{ color: "green" }}>
                        <ArrowUpOutlined style={{ marginRight: 4 }} />
                        {change}%
                      </span>
                    );
                  }

                  if (change < 0) {
                    return (
                      <span style={{ color: "red" }}>
                        <ArrowDownOutlined style={{ marginRight: 4 }} />
                        {change}%
                      </span>
                    );
                  }

                  return <span style={{ color: "black" }}>{change}%</span>;
                }}
                sorter={(a, b) => {
                  const changeA =
                    parseFloat(a.riderOnline?.percentage_change) || 0;
                  const changeB =
                    parseFloat(b.riderOnline?.percentage_change) || 0;
                  return changeA - changeB;
                }}
              />
            </ColumnGroup>

            <ColumnGroup title="Delivered">
              <Column
                title="Prev."
                dataIndex="prev_delivered_count"
                key="prev_delivered_count"
                render={(_, record) =>
                  record.orderCounts?.delivered?.prev_count ?? "-"
                }
                sorter={(a, b) => {
                  const prevCountA = a.orderCounts?.delivered?.prev_count || 0;
                  const prevCountB = b.orderCounts?.delivered?.prev_count || 0;
                  return prevCountA - prevCountB;
                }}
              />
              <Column
                title="Curr."
                dataIndex="curr_delivered_count"
                key="curr_delivered_count"
                render={(_, record) =>
                  record.orderCounts?.delivered?.curr_count ?? "-"
                }
                sorter={(a, b) => {
                  const currCountA = a.orderCounts?.delivered?.curr_count || 0;
                  const currCountB = b.orderCounts?.delivered?.curr_count || 0;
                  return currCountA - currCountB;
                }}
              />
              <Column
                title="Chg %"
                dataIndex="delivered_change_percentage"
                key="delivered_change_percentage"
                render={(_, record) => {
                  const change = parseFloat(
                    record.orderCounts?.delivered?.percentage_change
                  );

                  if (isNaN(change)) {
                    return "-";
                  }

                  if (change > 0) {
                    return (
                      <span style={{ color: "green" }}>
                        <ArrowUpOutlined style={{ marginRight: 4 }} />
                        {change}%
                      </span>
                    );
                  }

                  if (change < 0) {
                    return (
                      <span style={{ color: "red" }}>
                        <ArrowDownOutlined style={{ marginRight: 4 }} />
                        {change}%
                      </span>
                    );
                  }

                  // if 0
                  return <span style={{ color: "black" }}>{change}%</span>;
                }}
                sorter={(a, b) => {
                  const changeA =
                    parseFloat(a.orderCounts?.delivered?.percentage_change) ||
                    0;
                  const changeB =
                    parseFloat(b.orderCounts?.delivered?.percentage_change) ||
                    0;
                  return changeA - changeB; // Ascending order
                }}
              />
            </ColumnGroup>

            <ColumnGroup title="Undelivered">
              <Column
                title="Prev."
                dataIndex="prev_undelivered_count"
                key="prev_undelivered_count"
                render={(_, record) =>
                  record.orderCounts?.undelivered?.prev_count ?? "-"
                }
                sorter={(a, b) => {
                  const prevA = a.orderCounts?.undelivered?.prev_count || 0;
                  const prevB = b.orderCounts?.undelivered?.prev_count || 0;
                  return prevA - prevB;
                }}
              />
              <Column
                title="Curr."
                dataIndex="curr_undelivered_count"
                key="curr_undelivered_count"
                render={(_, record) =>
                  record.orderCounts?.undelivered?.curr_count ?? "-"
                }
                sorter={(a, b) => {
                  const currA = a.orderCounts?.undelivered?.curr_count || 0;
                  const currB = b.orderCounts?.undelivered?.curr_count || 0;
                  return currA - currB;
                }}
              />
              <Column
                title="Chg %"
                dataIndex="undelivered_change_percentage"
                key="undelivered_change_percentage"
                render={(_, record) => {
                  const change = parseFloat(
                    record.orderCounts?.undelivered?.percentage_change
                  );

                  if (isNaN(change)) {
                    return "-";
                  }

                  if (change < 0) {
                    // Undelivered count decreased = good = green + DOWN arrow
                    return (
                      <span style={{ color: "green" }}>
                        <ArrowDownOutlined style={{ marginRight: 4 }} />
                        {Math.abs(change)}%
                      </span>
                    );
                  }

                  if (change > 0) {
                    // Undelivered count increased = bad = red + UP arrow
                    return (
                      <span style={{ color: "red" }}>
                        <ArrowUpOutlined style={{ marginRight: 4 }} />
                        {Math.abs(change)}%
                      </span>
                    );
                  }

                  return <span style={{ color: "black" }}>{change}%</span>;
                }}
                sorter={(a, b) => {
                  const changeA =
                    parseFloat(a.orderCounts?.undelivered?.percentage_change) ||
                    0;
                  const changeB =
                    parseFloat(b.orderCounts?.undelivered?.percentage_change) ||
                    0;
                  return changeA - changeB; // Ascending order
                }}
              />
            </ColumnGroup>

            <ColumnGroup title="Cancelled">
              <Column
                title="Prev."
                dataIndex="prev_cancelled_count"
                key="prev_cancelled_count"
                render={(_, record) =>
                  record.orderCounts?.cancelled?.prev_count ?? "-"
                }
                sorter={(a, b) => {
                  const prevA = a.orderCounts?.cancelled?.prev_count || 0;
                  const prevB = b.orderCounts?.cancelled?.prev_count || 0;
                  return prevA - prevB; // Ascending order
                }}
              />
              <Column
                title="Curr."
                dataIndex="curr_cancelled_count"
                key="curr_cancelled_count"
                render={(_, record) =>
                  record.orderCounts?.cancelled?.curr_count ?? "-"
                }
                sorter={(a, b) => {
                  const currA = a.orderCounts?.cancelled?.curr_count || 0;
                  const currB = b.orderCounts?.cancelled?.curr_count || 0;
                  return currA - currB; // Ascending order
                }}
              />
              <Column
                title="Chg %"
                dataIndex="cancelled_change_percentage"
                key="cancelled_change_percentage"
                render={(_, record) => {
                  const change = parseFloat(
                    record.orderCounts?.cancelled?.percentage_change
                  );

                  if (isNaN(change)) {
                    return "-";
                  }

                  if (change < 0) {
                    // Cancelled count decreased = good
                    return (
                      <span style={{ color: "green" }}>
                        <ArrowDownOutlined style={{ marginRight: 4 }} />
                        {Math.abs(change)}%
                      </span>
                    );
                  }

                  if (change > 0) {
                    // Cancelled count increased = bad
                    return (
                      <span style={{ color: "red" }}>
                        <ArrowUpOutlined style={{ marginRight: 4 }} />
                        {Math.abs(change)}%
                      </span>
                    );
                  }

                  // No change
                  return <span style={{ color: "black" }}>{change}%</span>;
                }}
                sorter={(a, b) => {
                  const changeA =
                    parseFloat(a.orderCounts?.cancelled?.percentage_change) ||
                    0;
                  const changeB =
                    parseFloat(b.orderCounts?.cancelled?.percentage_change) ||
                    0;
                  return changeA - changeB; // Ascending order
                }}
              />
            </ColumnGroup>

            <Column
              title="Dory Order Attempt"
              dataIndex="dory_order_attempt_count"
              key="dory_order_attempt_count"
              render={() => "N/A"}
            />

            <Column
              title="CTO Order Attempt"
              dataIndex="ctoOrderCount"
              key="ctoOrderCount"
              sorter={(a, b) => a.ctoOrderCount - b.ctoOrderCount}
            />
          </Table>
        </div>
      </LayoutContent>
    );
  };

  return (
    <>
      <Wrapper content={renderContent()} />
    </>
  );
};

export default CityMonitoring;
